import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { LogOut, Sun, Moon, Boxes, History, ClipboardList, MessageSquareHeart, Gift } from 'lucide-react';
import { ProfileModal } from '../components/ProfileModal';
import { MarketingInventoryTab } from '../components/marketing/MarketingInventoryTab';
import { AssignmentHistoryTab } from '../components/marketing/AssignmentHistoryTab';
import { SiteVisitFeedbackTab } from '../components/marketing/SiteVisitFeedbackTab';
import { MemberFeedbackTab } from '../components/marketing/MemberFeedbackTab';
import { GiftPassTab } from '../components/marketing/GiftPassTab';

const MarketingOpsDashboard = () => {
  const { user, api, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [activeView, setActiveView] = useState('inventory');
  const [showProfileModal, setShowProfileModal] = useState(false);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0b0d]' : 'bg-[#f7f3e8]'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-[#f7f3e8] border-[#e5dfd0]'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="Clayworks" className="h-8 sm:h-9 w-auto" />
            <div>
              <h1 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Marketing Ops Dashboard</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Toggle theme">
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setShowProfileModal(true)} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Profile" data-testid="sidebar-profile-btn">
              {user?.photo_url ? (
                <img src={user.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">{user?.name?.charAt(0)}</div>
              )}
            </button>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center gap-1 overflow-x-auto clay-scroll-x">
          <button
            onClick={() => setActiveView('inventory')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'inventory'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="mo-tab-inventory"
          >
            <Boxes className="w-4 h-4 inline mr-1.5" />
            Inventory
          </button>
          <button
            onClick={() => setActiveView('history')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'history'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="mo-tab-history"
          >
            <History className="w-4 h-4 inline mr-1.5" />
            Assignment History
          </button>
          <button
            onClick={() => setActiveView('site_visits')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'site_visits'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="mo-tab-site-visits"
          >
            <ClipboardList className="w-4 h-4 inline mr-1.5" />
            Site Visit Feedback
          </button>
          <button
            onClick={() => setActiveView('member_feedback')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'member_feedback'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="mo-tab-member-feedback"
          >
            <MessageSquareHeart className="w-4 h-4 inline mr-1.5" />
            Member Feedback
          </button>
          <button
            onClick={() => setActiveView('gift_passes')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'gift_passes'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="mo-tab-gift-passes"
          >
            <Gift className="w-4 h-4 inline mr-1.5" />
            Gift Passes
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 space-y-6 sm:space-y-8">
        {activeView === 'history' ? (
          <AssignmentHistoryTab api={api} />
        ) : activeView === 'site_visits' ? (
          <SiteVisitFeedbackTab api={api} />
        ) : activeView === 'member_feedback' ? (
          <MemberFeedbackTab api={api} />
        ) : activeView === 'gift_passes' ? (
          <GiftPassTab api={api} />
        ) : (
          <MarketingInventoryTab api={api} />
        )}
      </div>

      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        api={api}
        onProfileUpdate={() => window.location.reload()}
      />
    </div>
  );
};

export default MarketingOpsDashboard;
